import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PlayerTracker } from './PlayerTracker';
import { getPlayerColor } from './sportConfigs';

/**
 * GazeOverlayV2 — Lightweight gaze overlay that attaches to an existing
 * video element. Drop-in replacement for the old GazeOverlay.jsx.
 *
 * Uses MediaPipe PoseLandmarker (client-side) for multi-player gaze
 * detection. Renders gaze beams on a canvas overlay.
 *
 * Compatible with SlamMobileAnalyzer's video pipeline — doesn't own
 * the video element, just reads frames from it and draws overlays.
 *
 * @param {HTMLVideoElement} videoEl - Reference to the existing video element
 * @param {number} width - Overlay container width
 * @param {number} height - Overlay container height
 * @param {boolean} visible - Whether overlay is visible
 * @param {string} sport - Sport key for config
 * @param {number} numPoses - Max simultaneous poses (default 10)
 */

// Shared MediaPipe loader
let mediapipePromise = null;
function loadMediaPipe() {
  if (!mediapipePromise) {
    mediapipePromise = import('@mediapipe/tasks-vision').then(async (mod) => {
      const vision = await mod.FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );
      return { vision, PoseLandmarker: mod.PoseLandmarker };
    });
  }
  return mediapipePromise;
}

/**
 * 1-Euro filter for smoothing.
 */
class EuroFilter {
  constructor(minCutoff = 1.0, beta = 0.01) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }
  apply(x, t) {
    if (this.tPrev === null) { this.xPrev = x; this.tPrev = t; return x; }
    const te = t - this.tPrev;
    if (te <= 0) return this.xPrev;
    const dx = (x - this.xPrev) / te;
    const dAlpha = 1.0 / (1.0 + 1.0 / (2.0 * Math.PI * 1.0 * te));
    const dxHat = dAlpha * dx + (1 - dAlpha) * this.dxPrev;
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const alpha = 1.0 / (1.0 + 1.0 / (2.0 * Math.PI * cutoff * te));
    const xHat = alpha * x + (1 - alpha) * this.xPrev;
    this.xPrev = xHat;
    this.dxPrev = dxHat;
    this.tPrev = t;
    return xHat;
  }
}

export default function GazeOverlayV2({
  videoEl,
  width = 0,
  height = 0,
  visible = true,
  sport = 'general',
  numPoses = 10,
}) {
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const trackerRef = useRef(new PlayerTracker(numPoses));
  const filtersRef = useRef(new Map());
  const requestRef = useRef(null);
  const lastTimeRef = useRef(-1);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize MediaPipe when visible
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setIsLoading(true);

    async function init() {
      try {
        const { vision, PoseLandmarker } = await loadMediaPipe();
        const lm = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numPoses,
          minPoseDetectionConfidence: 0.2,
          minPosePresenceConfidence: 0.2,
          minTrackingConfidence: 0.2,
        });
        if (active) {
          landmarkerRef.current = lm;
          setIsReady(true);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[GazeOverlayV2] Model load failed:', err);
        if (active) setIsLoading(false);
      }
    }

    if (!landmarkerRef.current) {
      init();
    } else {
      setIsReady(true);
      setIsLoading(false);
    }

    return () => { active = false; };
  }, [visible, numPoses]);

  // Render loop
  const renderLoop = useCallback(() => {
    if (!visible || !videoEl || !canvasRef.current || !landmarkerRef.current) {
      requestRef.current = requestAnimationFrame(renderLoop);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoEl;

    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    if (ctx && video.readyState >= 2 && video.currentTime !== lastTimeRef.current) {
      lastTimeRef.current = video.currentTime;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      try {
        const results = landmarkerRef.current.detectForVideo(video, performance.now());

        if (results.landmarks && results.landmarks.length > 0) {
          const assignments = trackerRef.current.update(results.landmarks);
          const t = video.currentTime;

          // Scale factors: MediaPipe gives [0..1], we need canvas pixels
          const sx = canvas.width;
          const sy = canvas.height;

          for (const { playerId, playerIndex, landmarks } of assignments) {
            const nose = landmarks[0];
            const leftEye = landmarks[2];
            const rightEye = landmarks[5];
            const leftEar = landmarks[7];
            const rightEar = landmarks[8];

            if (!nose || !leftEye || !rightEye || !leftEar || !rightEar) continue;

            const faceCenterX = (leftEar.x + rightEar.x) / 2;
            const faceCenterY = (leftEar.y + rightEar.y) / 2;
            const dx = (nose.x - faceCenterX) * 5;
            const dy = (nose.y - faceCenterY) * 5;

            const rawOx = ((leftEye.x + rightEye.x) / 2) * sx;
            const rawOy = ((leftEye.y + rightEye.y) / 2) * sy;
            const rawTx = rawOx + dx * sx;
            const rawTy = rawOy + dy * sy;

            // Get or create filters
            if (!filtersRef.current.has(playerId)) {
              filtersRef.current.set(playerId, {
                ox: new EuroFilter(1.5, 0.005), oy: new EuroFilter(1.5, 0.005),
                tx: new EuroFilter(0.8, 0.01), ty: new EuroFilter(0.8, 0.01),
              });
            }
            const f = filtersRef.current.get(playerId);
            const ox = f.ox.apply(rawOx, t);
            const oy = f.oy.apply(rawOy, t);
            const tx = f.tx.apply(rawTx, t);
            const ty = f.ty.apply(rawTy, t);

            const color = getPlayerColor(playerIndex);

            ctx.save();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.globalAlpha = 0.8;

            // Gaze beam
            ctx.beginPath();
            ctx.moveTo(ox, oy);
            ctx.lineTo(tx, ty);
            ctx.stroke();

            // Eye dots
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(leftEye.x * sx, leftEye.y * sy, 2, 0, Math.PI * 2);
            ctx.arc(rightEye.x * sx, rightEye.y * sy, 2, 0, Math.PI * 2);
            ctx.fill();

            // Target crosshair
            ctx.beginPath();
            ctx.moveTo(tx - 6, ty); ctx.lineTo(tx + 6, ty);
            ctx.moveTo(tx, ty - 6); ctx.lineTo(tx, ty + 6);
            ctx.stroke();

            // Player label
            ctx.font = 'bold 9px monospace';
            ctx.fillStyle = color;
            ctx.globalAlpha = 0.9;
            ctx.fillText(playerId, ox + 6, oy - 6);
            ctx.restore();
          }
        } else {
          trackerRef.current.update([]);
        }
      } catch (err) {
        // Silently handle inference errors in overlay mode
      }
    }

    requestRef.current = requestAnimationFrame(renderLoop);
  }, [visible, videoEl, width, height]);

  useEffect(() => {
    if (isReady && visible) {
      requestRef.current = requestAnimationFrame(renderLoop);
    }
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [isReady, visible, renderLoop]);

  if (!visible) return null;

  return (
    <>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none z-10"
        style={{ width, height }}
      />
      {isLoading && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-black/80 text-[10px] font-medium px-3 py-1 rounded-full text-amber-400">
          Loading gaze model...
        </div>
      )}
    </>
  );
}
